import React from 'react';
import styled from 'styled-components';
import { ImageSlider } from './ImageSlider';
import { CandidatreeDescription } from './CandidatreeDescription';
import { Label, Title } from '../../../../shared/styled-components/styled_text';
import { FlexCol } from '../../../../shared/FlexCol';
import { FlexRow } from '../../../../shared/FlexRow';
import { CompanyInfoContainer } from './CompanyInfoContainer';
import { TreeList } from './TreeList';

export interface Tree {
  workspace: {
    contact: {
      organization_name: string;
      officer_contact: string;
    };
    location: {
      address: string;
      address_detail?: string;
    };
  };
  title: string;
  content: string;
  media: { file?: { path_full: string }; icon?: string }[];
  posts: {
    id: number;
    title: string;
    team: { name: string };
    part: { name: string };
    end?: Date | null;
    is_activated: boolean;
  }[];
}

const tree: Tree = {
  workspace: {
    contact: {
      organization_name: '주식회사 솔라시스템컴퍼니',
      officer_contact: '1899-1057',
    },
    location: {
      address: '대전광역시 서구 만년로68번길 15-20',
      address_detail: '5층 캔디데이트',
    },
  },
  title: '[신입/경력 공개 채용] 주식회사 솔라시스템 컴퍼니',
  content:
    '나의 커리어가 업계의 기준이 되는 설레이는 경험. 솔라시스템컴퍼니에서는 가능합니다. 당신의 커리어가 새로운 역사가 됩니다. 솔라시스템은 사용자 중심의 서비스를 제공하고 있습니다. 기술혁신을 통해 인사담당자와 지원자의 불편함을 줄이기 위해 노력하며, 더 넓은 범위의 고객을 포용하고자 합니다.',
  media: [
    { file: { path_full: '/images/workspace-wizard/apply_homepage.svg' }, icon: 'home' },
    { file: { path_full: '/images/workspace-wizard/apply_blog.svg' }, icon: 'blog' },
    { file: { path_full: '/images/workspace-wizard/apply_youtube.svg' }, icon: 'youtube' },
  ],
  posts: [
    {
      id: 1,
      title: '혁신개발사업부 경력 기획자 모집',
      team: { name: '혁신개발사업부' },
      part: { name: '기획자' },
      is_activated: true,
    },
    {
      id: 2,
      title: '(경력무관) 백엔드 개발자',
      team: { name: '혁신서비스개발부' },
      part: { name: '백엔드 개발자' },
      end: new Date('2025-10-24'),
      is_activated: true,
    },
    {
      id: 3,
      title: 'E-비즈니스사업부 클라우드 엔지니어',
      team: { name: 'E-비즈니스사업부' },
      part: { name: '클라우드 엔지니어' },
      end: new Date('2025-09-04'),
      is_activated: true,
    },
  ],
};

export const ApplicationPreview: React.FC = () => {
  return (
    <Container>
      <Wrapper>
        <FlexRow width={'100%'} justify={'center'} align={'center'} style={{ height: '26.6rem' }}>
          <FlexRow width={'100%'} justify={'center'} align={'center'} style={{ height: '26.6rem' }}>
            <ImageSlider />
          </FlexRow>
        </FlexRow>
        <CandidatreeInfoContainer>
          <LogoImg src={'/images/workspace-wizard/apply_sample_logo.png'} alt='회사 로고' />
          <Label size={'s'} style={{ color: 'var(--content-color-secondary)' }}>
            {tree.workspace.contact?.organization_name}
          </Label>
          <Title size={'m'}>{tree.title}</Title>
          <FlexCol width={'100%'} justify={'flex-start'} align={'flex-start'} gap={'0.2rem'}>
            <CandidatreeDescription description={tree.content} />
          </FlexCol>
        </CandidatreeInfoContainer>
        <TreeList tree={tree} />
        <FlexCol
          width={'100%'}
          justify={'flex-start'}
          align={'flex-start'}
          gap={'1.2rem'}
          style={{ padding: '2.4rem 2.4rem 3.2rem 2.4rem', zIndex: 1, backgroundColor: 'var(--background-1)' }}
        >
          <FlexRow width={'100%'} justify={'flex-start'} align={'center'} gap={'0.6rem'}>
            <CompanyInfoLogo src={'/images/workspace-wizard/apply_sample_logo.png'} alt='회사 로고' />
            <Title size={'s'}>{tree.workspace.contact.organization_name}</Title>
          </FlexRow>
          <FlexCol width={'100%'} justify={'flex-start'} align={'flex-start'} gap={'0.8rem'}>
            <CompanyInfoContainer title={'📞 회사 연락처'} content={tree.workspace.contact.officer_contact} isPhone={true} />
            <CompanyInfoContainer
              title={'🏢 주소'}
              content={`${tree.workspace.location?.address} ${tree.workspace.location?.address_detail}`}
            />
          </FlexCol>
        </FlexCol>
        <FlexRow
          width={'100%'}
          justify={'center'}
          align={'center'}
          style={{
            paddingTop: '3rem',
            paddingBottom: '10rem',
            backgroundColor: 'var(--background-1)',
          }}
        >
          <FlexRow width={'100%'} justify={'center'} align={'center'}>
            <img style={{ width: '71px' }} src={'/images/workspace-wizard/powered_by_candidate.svg'} alt={'Powered by Candidate'} />
          </FlexRow>
        </FlexRow>
      </Wrapper>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  min-height: 600px;
  height: 100vh;
  height: 100svh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Wrapper = styled.div`
  min-width: 37.5rem;
  height: 100%;
  max-height: 100%;
  overflow: auto;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  -ms-overflow-style: none;

  ::-webkit-scrollbar {
    display: none;
  }
`;

const CandidatreeInfoContainer = styled.div`
  width: 100%;
  background-color: white;
  padding: 4.5rem 2.4rem 4rem;
  position: relative;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  transform: translateY(-2.4rem);

  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const LogoImg = styled.img`
  width: 7.5365rem;
  height: 7.5365rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  object-fit: cover;

  position: absolute;
  top: -4.3rem;
  left: 2.4rem;
  background-color: white;
`;

const CompanyInfoLogo = styled.img`
  width: 3.2rem;
  height: 3.2rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 1.2rem;
  color: white;
  font-weight: 700;
  line-height: 140%;
`;
